import React, { useState, useEffect, useRef } from 'react';
import { Room, RoomEvent, Track, ConnectionState } from 'livekit-client';
import { fetchLiveKitToken } from '../../utils/livekitService';
import './LiveKitVoiceAssistant.css';

/**
 * LiveKit Voice Assistant Component
 * Provides voice assistant functionality with LiveKit (without avatar)
 * Features:
 * - LiveKit room connection
 * - Audio visualization
 * - Transcript display
 * - Connection status
 * 
 * @param {string} tokenUrl - Optional URL to fetch LiveKit token from (defaults to production URL)
 */
function LiveKitVoiceAssistant({ tokenUrl = null }) {
  const [room, setRoom] = useState(null);
  const [connectionState, setConnectionState] = useState(ConnectionState.Disconnected);
  const [transcripts, setTranscripts] = useState([]);
  const [status, setStatus] = useState('Click "Start Call" to begin');
  const [isMuted, setIsMuted] = useState(false);
  
  // Audio analysis refs
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const visualizerCanvasRef = useRef(null);
  const animationFrameRef = useRef(null);
  const transcriptsRef = useRef(null);

  // Initialize transcripts ref
  useEffect(() => {
    transcriptsRef.current = transcripts;
  }, [transcripts]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (room) {
        room.disconnect().catch(console.error);
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close().catch(console.error);
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [room]);

  // Setup audio visualization
  const setupAudioAnalysis = (audioElement) => {
    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContext();
      console.log('✓ Audio context created');
    }

    const source = audioContextRef.current.createMediaElementSource(audioElement);
    
    analyserRef.current = audioContextRef.current.createAnalyser();
    analyserRef.current.fftSize = 256;

    source.connect(analyserRef.current);
    analyserRef.current.connect(audioContextRef.current.destination);

    console.log('✓ Audio analyser connected');

    const canvas = visualizerCanvasRef.current;
    if (canvas) {
      canvas.width = canvas.clientWidth;
      canvas.height = canvas.clientHeight;
      drawVisualizer();
    }
  };

  // Draw audio visualizer
  const drawVisualizer = () => {
    if (!analyserRef.current || !visualizerCanvasRef.current) return;

    const canvas = visualizerCanvasRef.current;
    const ctx = canvas.getContext('2d');
    const analyser = analyserRef.current;

    animationFrameRef.current = requestAnimationFrame(drawVisualizer);

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    analyser.getByteFrequencyData(dataArray);

    ctx.fillStyle = '#0f0f23';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const barWidth = (canvas.width / bufferLength) * 2.5;
    let x = 0;

    for (let i = 0; i < bufferLength; i++) {
      const barHeight = (dataArray[i] / 255) * canvas.height;
      ctx.fillStyle = `rgb(50, ${dataArray[i] + 100}, 150)`;
      ctx.fillRect(x, canvas.height - barHeight, barWidth, barHeight);
      x += barWidth + 1;
    }
  };

  // Add transcript
  const addTranscript = (text, type) => {
    setTranscripts(prev => [...prev, { text, type, timestamp: new Date() }]);
    
    // Auto-scroll to bottom
    setTimeout(() => {
      const container = document.getElementById('transcripts-container');
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    }, 100);
  };

  // Connect to LiveKit
  const connect = async () => {
    try {
      setStatus('Connecting...');

      // fetchLiveKitToken will use production URL by default if tokenUrl is null
      const { token, url } = await fetchLiveKitToken(tokenUrl);
      const newRoom = new Room();

      // Connection state handler
      newRoom.on(RoomEvent.ConnectionStateChanged, (state) => {
        setConnectionState(state);
        setStatus(`Connection: ${state}`);
        
        if (state === ConnectionState.Connected) {
          setStatus('Connected - Speak now!');
          console.log('✓ Connected to LiveKit');
        }
      });

      // Track subscription handler
      newRoom.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
        if (track.kind === Track.Kind.Audio) {
          console.log(`✓ Audio track received from ${participant.identity}`);
          
          const audioElement = track.attach();
          audioElement.style.display = 'none';
          document.body.appendChild(audioElement);
          
          setupAudioAnalysis(audioElement);
        }
      });

      // Data received handler
      newRoom.on(RoomEvent.DataReceived, (payload, participant) => {
        try {
          const data = JSON.parse(new TextDecoder().decode(payload));
          if (data.type === 'transcription') {
            const isAgent = participant.identity.startsWith('agent') || 
                           participant.identity.includes('agent');
            addTranscript(data.text, isAgent ? 'agent' : 'user');
          }
        } catch (e) {
          console.error('Error parsing data:', e);
        }
      });

      // Transcription received handler
      newRoom.on(RoomEvent.TranscriptionReceived, (segments, participant) => {
        segments.forEach(segment => {
          if (segment.final) {
            const isAgent = participant?.identity?.startsWith('agent') || 
                           participant?.identity?.includes('agent');
            addTranscript(segment.text, isAgent ? 'agent' : 'user');
          }
        });
      });

      await newRoom.connect(url, token);
      await newRoom.localParticipant.setMicrophoneEnabled(true);
      setIsMuted(false);
      
      setRoom(newRoom);
      console.log('✓ Microphone enabled');

    } catch (error) {
      console.error('Connection error:', error);
      setStatus(`Error: ${error.message}`);
    }
  };

  // Disconnect from LiveKit
  const disconnect = async () => {
    try {
      if (room) {
        await room.disconnect();
        setRoom(null);
      }

      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        await audioContextRef.current.close();
        audioContextRef.current = null;
      }

      if (analyserRef.current) {
        analyserRef.current = null;
      }

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      // Clear visualizer
      const canvas = visualizerCanvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }

      setConnectionState(ConnectionState.Disconnected);
      setStatus('Disconnected');
      setIsMuted(false);
      console.log('Disconnected');
    } catch (error) {
      console.error('Error during disconnect:', error);
      setStatus(`Disconnect error: ${error.message}`);
    }
  };

  // Toggle microphone mute/unmute
  const toggleMute = async () => {
    if (!room || !isConnected) return;
    
    try {
      const newMuteState = !isMuted;
      await room.localParticipant.setMicrophoneEnabled(!newMuteState);
      setIsMuted(newMuteState);
      console.log(newMuteState ? '🔇 Microphone muted' : '🎤 Microphone unmuted');
    } catch (error) {
      console.error('Error toggling microphone:', error);
    }
  };

  const isConnected = connectionState === ConnectionState.Connected;

  return (
    <div className="livekit-voice-assistant">
      <div className="voice-assistant-container">
        <div className="voice-assistant-sidebar">
          <h1>🎙️ Voice Assistant</h1>
          
          <button 
            id="connectBtn"
            onClick={connect}
            disabled={isConnected}
            className="connect-btn app-btn"
          >
            Start Call
          </button>
          
          <button 
            id="disconnectBtn"
            onClick={disconnect}
            disabled={!isConnected}
            className="disconnect-btn app-btn"
            style={{ display: isConnected ? 'inline-block' : 'none' }}
          >
            End Call
          </button>
          
          <button 
            id="muteBtn"
            onClick={toggleMute}
            disabled={!isConnected}
            className={`mute-btn ${isMuted ? 'muted' : ''}`}
            style={{ display: isConnected ? 'inline-block' : 'none' }}
          >
            {isMuted ? '🔇 Unmute' : '🎤 Mute'}
          </button>
          
          <div id="status" className="status-display">
            {status}
          </div>
          
          <canvas 
            ref={visualizerCanvasRef}
            id="visualizer"
            className="audio-visualizer"
          />
          
          <div id="transcripts-container" className="transcripts-container">
            {transcripts.length === 0 ? (
              <div className="transcript-placeholder">Transcripts will appear here...</div>
            ) : (
              transcripts.map((transcript, index) => (
                <div 
                  key={index}
                  className={`transcript ${transcript.type}`}
                >
                  <span className="transcript-label">
                    {transcript.type === 'agent' ? '🤖 Agent' : '👤 You'}:
                  </span>
                  <span className="transcript-text">{transcript.text}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default LiveKitVoiceAssistant;

